/* Settings owned by roles (Phase 7): X4, X5, F2, SL9, SET1, Elections & letters, Tax estimate.
   Tokens only (no raw colours); every control keeps the 44 px target. */

/* On a tinted ground (an open row, the chosen tile or radio), muted text steps up a shade so it
   keeps 4.5:1. */
.rowOpen,
.editRow,
.tileOn,
.radio[data-selected] {
  --text-muted: var(--text-secondary);
}

/* ---- Layout ---- */
.stack {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* Two cards side by side that wrap on narrow screens. */
.pair {
  composes: field-row from global;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(320px, 100%), 1fr));
  gap: 24px;
  align-items: start;
}

/* Owner settings: a sidebar beside the page (NAV2 option B). It sits beside the page only when
   the page itself has room for both, measured on the page so an open Help panel counts as well as
   the window; otherwise one "Settings sections" button opens it above the page (APP-094 review). */
.sidebarHost {
  container: settings-body / inline-size;
}

.withSidebar {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 24px;
  align-items: start;
}

@container settings-body (min-width: 1200px) {
  .withSidebar {
    grid-template-columns: 240px minmax(0, 1fr);
  }
}

.sidebar {
  min-width: 0;
}

.navigation {
  min-width: 0;
}
.mobileMenu {
  display: none;
}

@container settings-body (max-width: 1199.98px) {
  .mobileMenu {
    display: block;
  }
  .navigation:not([data-expanded='true']) .sectionLinks {
    display: none;
  }
  .sidebar {
    position: static;
  }
}

.sideMain {
  display: flex;
  flex-direction: column;
  gap: 24px;
  min-width: 0;
}

.sectorPicker {
  max-width: 360px;
  margin-bottom: 24px;
}

/* ---- Text ---- */
.body {
  margin: 0;
  color: var(--text-secondary);
  font-size: 14px;
  line-height: 1.6;
}

.muted {
  margin: 0;
  color: var(--text-muted);
  font-size: 13px;
  line-height: 1.5;
}

.strong {
  color: var(--text-heading);
  font-weight: var(--fw-semibold);
}

.sub {
  display: block;
  color: var(--text-muted);
  font-size: 12px;
  font-weight: var(--fw-regular);
  line-height: 1.45;
}

.footnote {
  margin: 0;
  color: var(--text-muted);
  font-size: 12px;
  line-height: 1.5;
}

.notice {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 10px 14px;
  border-radius: var(--radius-sm);
  background: var(--surface-urgent);
  color: var(--text-body);
  font-size: 14px;
}

/* A note under a template that doesn't stop it being used (QA-044), with how to act on it. */
.templateNote {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 6px;
}

.templateNoteText {
  display: flex;
  gap: 6px;
  align-items: flex-start;
  margin: 0;
  color: var(--status-waiting-ink);
  font-size: 13px;
  font-weight: var(--fw-semibold);
  line-height: 1.45;
}

.templateNoteText > svg {
  flex: none;
  margin-top: 1px;
}

.templateNoteHow {
  color: var(--text-secondary);
  font-size: 13px;
  line-height: 1.5;
}

.templateNoteHow > summary {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  color: var(--text-link);
  font-weight: var(--fw-semibold);
  text-decoration: underline;
  cursor: pointer;
}

.templateNoteHow > summary:focus-visible {
  outline: 2px solid var(--focus-ring-color);
  outline-offset: 2px;
}

.templateNoteHow > p {
  max-width: 62ch;
  margin: 0 0 6px;
}

.templateNoteHow code {
  padding: 0 3px;
  border-radius: 3px;
  background: var(--surface-soft);
  color: var(--text-heading);
  font-size: 12px;
}

.warning {
  margin: 0;
  padding: 10px 14px;
  border-left: 3px solid var(--status-problem-ink);
  border-radius: var(--radius-sm);
  background: var(--status-problem-bg);
  color: var(--status-problem-ink);
  font-size: 14px;
}

.bigValue {
  margin: 0;
  color: var(--text-heading);
  font-size: 48px;
  line-height: 1;
}

.eyebrowMuted {
  margin: 0;
  color: var(--text-muted);
}

.sectionLabel {
  margin: 0;
  color: var(--text-muted);
}

.code {
  font-family: var(--font-mono);
  font-size: 13px;
  white-space: nowrap;
}

.link {
  color: var(--text-link);
  text-decoration: underline;
}

.link[data-hovered] {
  color: var(--text-link-hover-on-light);
}

.up {
  color: var(--status-done-ink);
}

.down {
  color: var(--status-problem-ink);
}

/* "Over budget" under an amount in a numeric cell (QA-036): the words, not the colour, say it. */
.cellPill {
  display: block;
  margin-top: 4px;
}

/* "Last changed by … on … · History" */
.changed {
  margin: 0;
  color: var(--text-muted);
  font-size: 12px;
  line-height: 1.6;
}

/* The History button keeps its 44 px box (QA-043); see `.inlineLink`. */
.changed button {
  color: var(--text-link);
  font-size: 12px;
}

.rowChanged {
  margin-top: 2px;
  font-weight: var(--fw-regular);
}

.asideRow {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
}

/* ---- Forms ---- */
.panel {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 18px 20px;
  border: 1px solid var(--border-hairline);
  border-radius: var(--radius-sm);
  background: var(--surface-soft);
}

.panelTitle {
  margin: 0;
  color: var(--text-heading);
  font-size: 15px;
  font-weight: var(--fw-bold);
}

.fieldRow {
  composes: field-row from global;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(200px, 100%), 1fr));
  gap: 14px 16px;
  align-items: start;
}

.formActions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.nameField {
  max-width: 480px;
}

.inlineForm {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 420px;
}

.rowEditor {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 6px 0;
}

.renameForm {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 220px;
}

.stepperField {
  max-width: 260px;
}

.stepper {
  display: flex;
  align-items: stretch;
  gap: 6px;
}

.stepInput {
  text-align: center;
  font-size: 20px;
  font-weight: var(--fw-semibold);
}

.stepButton {
  display: grid;
  flex: none;
  place-items: center;
  width: var(--control-height);
  height: var(--control-height);
  border: 1px solid var(--border-control);
  border-radius: var(--radius-sm);
  background: var(--white);
  color: var(--brand-navy);
  cursor: pointer;
}

.stepButton[data-hovered] {
  border-color: var(--brand-cyan);
}

.stepButton[data-focus-visible] {
  outline: none;
  border-color: var(--brand-cyan);
  box-shadow: var(--focus-halo);
}

.stepButton[data-disabled] {
  color: var(--text-muted);
  cursor: default;
}

/* Checkbox chips (award types, reminder days). */
.typeGroup,
.checkGroup {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.typeGroup {
  flex-direction: row;
  flex-wrap: wrap;
}

.checkRow {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.typeChip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: var(--control-height);
  padding: 0 14px;
  border: 1px solid var(--border-control);
  border-radius: var(--radius-pill);
  background: var(--white);
  color: var(--text-body);
  font-size: 13px;
  font-weight: var(--fw-semibold);
  cursor: pointer;
}

.typeChip[data-selected] {
  border-color: var(--brand-navy);
  background: var(--brand-cyan-50);
  color: var(--brand-navy);
}

.typeChip[data-focus-visible] {
  outline: none;
  box-shadow: var(--focus-halo);
}

.typeChip[data-disabled] {
  cursor: default;
  opacity: 0.75;
}

.typeBox {
  display: grid;
  place-items: center;
  width: 18px;
  height: 18px;
  border: 2px solid var(--brand-navy);
  border-radius: 4px;
  background: var(--white);
  color: var(--brand-navy);
}

.typeChip[data-selected] .typeBox {
  background: var(--brand-navy);
  color: var(--white);
}

/* Radio cards (My settings › header). */
.radioGroup {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.radioRow {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(200px, 100%), 1fr));
  gap: 10px;
}

.radio {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  min-height: var(--control-height);
  padding: 12px 14px;
  border: 1px solid var(--border-control);
  border-radius: var(--radius-sm);
  background: var(--white);
  cursor: pointer;
}

.radio[data-selected] {
  border-color: var(--brand-cyan-600);
  background: var(--brand-cyan-50);
}

.radio[data-focus-visible] {
  outline: none;
  box-shadow: var(--focus-halo);
}

.radioDot {
  flex: none;
  width: 18px;
  height: 18px;
  margin-top: 2px;
  border: 2px solid var(--brand-navy);
  border-radius: 50%;
  background: var(--white);
}

.radio[data-selected] .radioDot {
  border-width: 6px;
  border-color: var(--brand-cyan-600);
}

/* People search. */
.picker {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.pickerHint {
  margin: 0;
  color: var(--text-secondary); /* 4.5:1 on white; --text-muted is lighter */
  font-size: 13px;
  line-height: 1.5;
}

.pickerLabel {
  color: var(--text-heading);
  font-size: 14px;
  font-weight: var(--fw-semibold);
}

.searchBox {
  position: relative;
  display: flex;
  align-items: center;
}

.searchIcon {
  position: absolute;
  left: 12px;
  color: var(--text-muted);
  pointer-events: none;
}

.searchInput {
  width: 100%;
  min-height: var(--control-height);
  padding: 0 12px 0 40px;
  border: 1px solid var(--border-control);
  border-radius: var(--radius-sm);
  background: var(--white);
  color: var(--text-body);
  font: inherit;
  font-size: 15px;
}

.searchInput[data-focused],
.searchInput:focus {
  outline: none;
  border-color: var(--brand-cyan);
  box-shadow: var(--focus-halo);
}

.pickerPopover {
  width: var(--trigger-width);
  min-width: min(320px, calc(100vw - 32px));
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid var(--border-card);
  border-radius: var(--radius-md);
  background: var(--white);
  box-shadow: var(--shadow-menu);
}

.pickerList {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px;
  outline: none;
}

.pickerOption {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
  min-height: 48px;
  padding: 6px 12px;
  border-radius: var(--radius-sm);
  cursor: pointer;
  outline: none;
}

.pickerOption[data-focused],
.pickerOption[data-hovered] {
  background: var(--brand-cyan-50);
}

.pickerName {
  color: var(--text-heading);
  font-size: 14px;
  font-weight: var(--fw-semibold);
}

.pickerMeta {
  color: var(--text-secondary);
  font-size: 12px;
}

.pickerEmpty {
  margin: 0;
  padding: 12px;
  color: var(--text-secondary);
  font-size: 14px;
}

.chosen {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.chosenName {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  color: var(--text-heading);
  font-weight: var(--fw-semibold);
}

.fileRow {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

/* ---- Tables ---- */
.tableWrap {
  position: relative;
  max-width: 100%;
  overflow-x: auto;
}

.tableWrap:focus-visible {
  outline-offset: -2px;
}

.table {
  width: 100%;
  /* Narrower than this, the table scrolls sideways inside its card instead of crushing columns. */
  min-width: 600px;
  border-collapse: collapse;
  font-size: 14px;
}

.table thead th {
  padding: 10px 12px;
  border-bottom: 1px solid var(--border-card);
  color: var(--text-muted);
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: var(--fw-medium);
  letter-spacing: 0.12em;
  text-align: left;
  text-transform: uppercase;
  white-space: nowrap;
}

.table td,
.table tbody th,
.table tfoot th {
  padding: 12px;
  border-bottom: 1px solid var(--border-hairline);
  text-align: left;
  vertical-align: middle;
}

.table tfoot th,
.table tfoot td {
  color: var(--text-heading);
  font-weight: var(--fw-bold);
}

.table tfoot th {
  color: var(--text-muted);
  font-family: var(--font-ui);
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.table .num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.rowHead {
  color: var(--text-heading);
  font-weight: var(--fw-semibold);
}

.poolsTable {
  min-width: 920px;
}

.ledgerTable {
  min-width: 860px;
}

/* APP-148 (Ledger board): who made the change, and the Executive it was made for. */
.who {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
}

.forPill {
  display: inline-flex;
  gap: 6px;
  align-items: center;
  padding: 2px 10px 2px 2px;
  border-radius: var(--radius-pill);
  background: var(--status-info-bg);
  color: var(--status-info-ink);
  font-size: 13px;
  font-weight: var(--fw-semibold);
  white-space: nowrap;
}

.actionsCell {
  white-space: nowrap;
}

.actionsCell > * + * {
  margin-left: 6px;
}

.nowrap {
  white-space: nowrap;
}

.rowOpen td,
.rowOpen th {
  border-bottom-color: transparent;
  background: var(--brand-cyan-50);
}

.editRow td {
  background: var(--brand-cyan-50);
}

.rowQuiet th,
.rowQuiet td {
  color: var(--text-muted);
}

.voided td {
  color: var(--text-muted);
  text-decoration: line-through;
}

.emptyRow td {
  padding: 28px 12px;
  background: var(--surface-soft);
  color: var(--text-secondary);
  text-align: center;
}

/* ---- Budget ledger ---- */
.tiles {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(150px, 100%), 1fr));
  gap: 10px;
}

.tile {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-height: var(--control-height);
  padding: 12px 14px;
  border: 1px solid var(--border-card);
  border-radius: var(--radius-sm);
  background: var(--white);
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.tile:hover {
  border-color: var(--brand-cyan);
}

.tile:focus-visible {
  outline: none;
  box-shadow: var(--focus-halo);
}

.tileOn {
  border: 2px solid var(--brand-navy);
  background: var(--brand-cyan-50);
}

.tileLabel {
  color: var(--text-muted);
}

.tileValue {
  color: var(--text-heading);
  font-size: 22px;
  line-height: 1.1;
}

.tileMeta {
  color: var(--text-secondary);
  font-size: 12px;
  line-height: 1.4;
}

.chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.chip {
  min-height: var(--control-height);
  padding: 0 14px;
  border: 1px solid var(--border-control);
  border-radius: var(--radius-pill);
  background: var(--white);
  color: var(--text-body);
  font: inherit;
  font-size: 13px;
  font-weight: var(--fw-semibold);
  cursor: pointer;
}

.chip:hover {
  border-color: var(--brand-cyan);
}

.chip:focus-visible {
  outline: none;
  box-shadow: var(--focus-halo);
}

.chipOn {
  border-color: var(--brand-navy);
  background: var(--brand-navy);
  color: var(--white);
}

.chipNote {
  margin-left: auto;
  color: var(--text-muted);
  font-size: 13px;
}

/* ---- TAA split ---- */
.splitBlock {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.splitNumbers {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 8px;
  color: var(--text-secondary);
  font-size: 14px;
}

.splitPct {
  color: var(--text-heading);
  font-size: 28px;
}

.splitBar {
  display: flex;
  gap: 2px;
  height: 10px;
}

.splitPaa,
.splitSector {
  border-radius: 4px;
}

.splitPaa {
  background: var(--brand-navy);
}

.splitSector {
  background: var(--brand-cyan);
}

/* ---- FMV ---- */
.fmvRow {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(220px, 100%), 1fr));
  gap: 20px;
  align-items: end;
}

.fmvNow {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.fmvChart {
  min-width: 0;
}

@media (min-width: 900px) {
  .fmvRow {
    grid-template-columns: minmax(200px, 1fr) minmax(0, 2fr);
  }
}

/* ---- Step chart (History drawer, FMV) ---- */
.overview {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--border-hairline);
}

.chart {
  margin: 0;
}

.chart svg {
  display: block;
  width: 100%;
  height: auto;
  overflow: visible;
}

.chartBase {
  stroke: var(--border-card);
  stroke-width: 1;
}

.chartStep {
  fill: none;
  stroke: var(--brand-petrol);
  stroke-width: 2;
  stroke-linejoin: round;
}

.chartMark {
  fill: var(--white);
  stroke: var(--brand-petrol);
  stroke-width: 2;
}

.chartMarkLast {
  fill: var(--brand-navy);
  stroke: var(--white);
}

.chartMarkAhead {
  fill: var(--white);
  stroke: var(--text-gold);
  stroke-dasharray: 2 2;
}

.chartToday {
  stroke: var(--text-muted);
  stroke-width: 1;
  stroke-dasharray: 3 3;
}

.chartAsOf {
  stroke: var(--brand-gold);
  stroke-width: 2;
}

.chartValue {
  fill: var(--text-heading);
  font-family: var(--font-body);
  font-size: 11px;
  font-weight: var(--fw-bold);
}

.chartNote {
  fill: var(--text-muted);
  font-family: var(--font-body);
  font-size: 10px;
}

/* Before the first value (APP-093): the real axis under a dashed grey staircase, and a caption
   that says what's true. The staircase is a pattern, so it stays quiet. */
.chartGhost {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.chartGhostStep {
  fill: none;
  stroke: var(--gray-300);
  stroke-width: 2;
  stroke-dasharray: 5 4;
  stroke-linejoin: round;
}

.chartCaption {
  margin: 0;
  color: var(--text-secondary);
  font-size: 13px;
  line-height: 1.5;
}

.asOf {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.asOfResult {
  margin: 0;
  padding: 10px 12px;
  border-left: 3px solid var(--brand-gold);
  border-radius: var(--radius-sm);
  background: var(--surface-soft);
  color: var(--text-body);
  font-size: 14px;
  line-height: 1.5;
}

/* ---- Lists ---- */
.settingList,
.facts {
  display: flex;
  flex-direction: column;
  gap: 0;
  margin: 0;
}

.settingItem,
.facts > div {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 4px 16px;
  padding: 10px 0;
  border-bottom: 1px solid var(--border-hairline);
}

.settingItem:last-child,
.facts > div:last-child {
  border-bottom: 0;
}

.settingItem dt,
.facts dt {
  color: var(--text-secondary);
  font-size: 14px;
}

.settingItem dd,
.facts dd {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin: 0;
  color: var(--text-heading);
  font-size: 14px;
  font-weight: var(--fw-semibold);
  text-align: right;
}

.bullets {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding-left: 18px;
  color: var(--text-secondary);
  font-size: 14px;
  line-height: 1.55;
}

.notes {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 0;
  color: var(--text-secondary);
  font-size: 14px;
  list-style: none;
}

.notes li {
  display: flex;
  gap: 10px;
  align-items: center;
}

.notes svg {
  flex: none;
  color: var(--brand-cyan-600);
}

.nameUses {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  border: 1px solid var(--border-card);
  border-radius: var(--radius-sm);
  list-style: none;
}

.nameUses li {
  display: grid;
  grid-template-columns: 18px minmax(120px, 1fr) minmax(0, 2fr) auto;
  gap: 8px 12px;
  align-items: center;
  padding: 10px 14px;
  border-bottom: 1px solid var(--border-hairline);
  font-size: 14px;
}

.nameUses li:last-child {
  border-bottom: 0;
  background: var(--surface-soft);
}

.nameUses svg {
  color: var(--brand-cyan-600);
}

/* On a phone each row stacks beside its icon: where, the example, then the pill (QA-008). */
@media (max-width: 640px) {
  .nameUses li {
    grid-template-columns: 18px minmax(0, 1fr);
    align-items: start;
  }

  .nameUses li > :not(svg) {
    grid-column: 2;
    justify-self: start;
  }
}

.choices {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: 14px;
}

/* ---- Delegates (SL9) ---- */
.delegates {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  list-style: none;
}

.delegate {
  display: grid;
  grid-template-columns: minmax(200px, 1.2fr) minmax(0, 1.4fr) auto;
  gap: 12px 20px;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid var(--border-hairline);
}

@media (max-width: 900px) {
  .delegate {
    grid-template-columns: minmax(0, 1fr);
  }
}

.delegateOff .delegateWho,
.delegateOff .typeGroup {
  opacity: 0.75;
}

.delegateWho {
  display: flex;
  gap: 12px;
  align-items: center;
  min-width: 0;
}

.avatar {
  display: grid;
  flex: none;
  place-items: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--brand-navy);
  color: var(--white);
  font-size: 13px;
  font-weight: var(--fw-bold);
}

.delegateName {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.delegateSwitches {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.delegateChanged {
  grid-column: 1 / -1;
}

.dangerButton {
  color: var(--status-problem-ink);
}

/* ---- Dark side cards ---- */
.darkBody {
  margin: 0;
  color: var(--text-on-dark-soft);
  font-size: 14px;
  line-height: 1.6;
}

.mutedOnDark {
  color: var(--text-on-dark-muted);
}

.darkEyebrow {
  margin: 0;
  color: var(--text-on-dark-muted);
}

.darkList {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
}

.darkList > div {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 4px 12px;
  padding: 10px 12px;
  border-radius: var(--radius-sm);
  background: var(--white-a08);
}

.darkList dt {
  color: var(--text-on-dark-soft);
  font-size: 14px;
}

.darkList dd {
  margin: 0;
  color: var(--white);
  font-weight: var(--fw-semibold);
}

.darkChanged {
  color: var(--text-on-dark-muted);
}

/* ---- Charge code example ---- */
.codeGrid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.codeBox {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 12px;
  border: 1px solid var(--border-card);
  border-radius: var(--radius-sm);
}

/* ---- Letter templates ---- */
.templateTypes {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.templateType {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.templates {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  border: 1px solid var(--border-card);
  border-radius: var(--radius-sm);
  list-style: none;
}

.template {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 10px 16px;
  align-items: center;
  padding: 12px 14px;
  border-bottom: 1px solid var(--border-hairline);
}

.template:last-child {
  border-bottom: 0;
}

.templateText {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.templateActions {
  display: flex;
  flex-wrap: wrap;
  grid-column: 1 / -1;
  gap: 8px;
  align-items: center;
}

.fileLink {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: var(--control-height);
  padding: 0 4px;
  color: var(--text-link);
  font-size: 14px;
  font-weight: var(--fw-semibold);
  text-decoration: underline;
}

.fileLink:hover {
  color: var(--text-link-hover-on-light);
}

/* The field wrapper around a chip or radio card adds nothing of its own. */
.chipField {
  display: contents;
}

/* SET1's pronoun choices: pills in a row, the chosen one navy with a tick. */
.chipRow {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.radioChip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: var(--control-height);
  padding: 0 16px;
  border: 1px solid var(--border-control);
  border-radius: var(--radius-pill);
  background: var(--white);
  color: var(--brand-navy);
  font-size: 14px;
  font-weight: var(--fw-medium);
  cursor: pointer;
}

.radioChip[data-selected] {
  border-color: var(--brand-navy);
  background: var(--brand-navy);
  color: var(--white);
  font-weight: var(--fw-semibold);
}

.radioChip[data-focus-visible] {
  outline: none;
  box-shadow: var(--focus-halo);
}

.radioChip[data-disabled] {
  cursor: default;
  opacity: 0.75;
}

/* ---- X5's side lists: requesters and Sector Leads ---- */
.people {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  list-style: none;
}

.person {
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid var(--border-hairline);
}

.person:last-child {
  border-bottom: 0;
}

.personName {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.personPill {
  align-self: flex-start;
}

/* ---- X4 day one (APP-093) ---- */
/* "Set all budget pools for 2026": the board's pale cyan callout with the pool art and one way in. */
.setupBanner {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 14px 18px;
  padding: 16px 18px;
  border: 1px solid color-mix(in srgb, var(--brand-cyan) 28%, var(--white));
  border-left: 3px solid var(--brand-cyan);
  border-radius: var(--radius-sm);
  background: color-mix(in srgb, var(--brand-cyan) 6%, var(--white));
}

.setupBannerWords {
  display: flex;
  flex: 1 1 360px;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.setupBannerTitle {
  margin: 0;
  color: var(--brand-navy);
  font-size: 16px;
  font-weight: var(--fw-bold);
  line-height: 1.35;
}

/* A table's one empty row holding a designed empty state: the state brings its own box. */
.emptyStateRow td {
  padding: 12px 0;
}

/* A value that isn't set yet, greyed (with the way to set it beside it when there is one). */
.notSetText {
  color: var(--text-muted);
  font-weight: var(--fw-regular);
}

/* A link on a navy card ("Charge code history"), a 44 px target by its box. The lighter cyan keeps
   4.5:1 on navy; hovered, it turns white and underlined, so the change shows in more than colour. */
.darkLink {
  display: inline-flex;
  align-self: flex-start;
  align-items: center;
  min-height: var(--control-height);
  color: var(--brand-cyan-400);
  font-size: 14px;
  font-weight: var(--fw-semibold);
  text-decoration: underline;
}

.darkLink[data-hovered],
.darkLink[data-focus-visible] {
  color: var(--white);
}

/* A value not set yet on a navy card: greyed, not the white of a set value. */
.darkList dd.notSetOnDark {
  color: var(--text-on-dark-muted);
  font-weight: var(--fw-regular);
}

/* APP-222: the reset dialog's sentences under its summary, spaced as the dialog's own rows. */
.dialogNotes {
  display: flex;
  flex-direction: column;
  gap: 12px;
  font-size: 15px;
  line-height: 1.6;
}

.dialogNotes p {
  margin: 0;
}

/* APP-268: the Spot Committee's rows, on Admin's member rows, with Remove in one column on the right. */
.committeeList {
  margin: 0;
}

.committeeRow {
  flex-wrap: wrap;
}

/* Above Admin's .memberText (flex: 1), or a narrow row squeezes the name to a letter a line. */
.committeeRow > .committeeWho {
  flex: 1 1 140px;
}

/* On a wrapped line it stays in the column on the right. */
.committeeAction {
  min-width: 84px;
  margin-left: auto;
}

.committeeGoneName {
  color: var(--text-secondary);
  text-decoration: line-through;
}

.committeeGoneAvatar {
  background: var(--text-secondary);
}

.unsavedNote {
  color: var(--text-secondary);
  font-size: 13px;
}

/* SENT-236: a draft whose setting changed under it. */
.outOfDateWords {
  margin: 0 0 8px;
}
